/**
 * The brand rules — Safaqat's two signature marks. THEME.md §3.2.
 *
 * Both are the same idea at two weights: the brand colours as a short run of
 * descending bars. They are how a surface says "Safaqat" without spending a
 * background on it, and they replace the earlier gold-bar-plus-red-swoosh rule.
 *
 * Owner's standard (2026-09-17), choosing this over three louder options:
 * every brand colour present, none of them shouting. The failure mode these
 * are designed against is the one the repo already hit twice — a four-colour
 * band scaled up until it becomes a slab of colour down the page. These are
 * FIXED-SIZE elements in absolute pixels. They do not stretch, and they must
 * never be given a percentage width or `preserveAspectRatio="none"`.
 *
 * Purely decorative (`aria-hidden`), carrying no information, which is what
 * lets the board's yellow run at full strength despite being 1.51:1 on white.
 */

type Tone = 'ink' | 'light';

/**
 * The section rule — under a heading, beneath hero copy, at the head of a card
 * group. Green leads and the accents trail off it.
 *
 * Blue is deliberately absent here: THEME.md §3 bars it from interface chrome,
 * and on white it is the one board colour that reads as a UI state (a link, an
 * info chip) rather than as brand.
 */
export function BrandRule({ tone = 'ink', className = '' }: { tone?: Tone; className?: string }) {
  const lead = tone === 'light' ? '#FFFFFF' : '#004D40';
  const red = tone === 'light' ? '#FF8A80' : '#DF2F2B';
  return (
    <svg
      viewBox="0 0 51 3"
      className={`block h-[3px] w-[51px] ${className}`}
      aria-hidden="true"
      focusable="false"
    >
      <rect x="0" y="0" width="30" height="3" rx="1.5" fill={lead} />
      <rect x="33" y="0" width="11" height="3" rx="1.5" fill="#FFC107" />
      <rect x="46" y="0" width="5" height="3" rx="1.5" fill={red} />
    </svg>
  );
}

/**
 * The full signature — all four board colours, including the blue that lives
 * nowhere else in the interface. Reserved for deep-green bands, where it is the
 * one mark on an otherwise solid field.
 *
 * `tone="light"` swaps the leading green bar for white: on `#002A23` the brand
 * green is 1.2:1 and simply disappears, which would leave a three-colour mark
 * pretending to be four.
 */
export function BrandSignature({ tone = 'light', className = '' }: { tone?: Tone; className?: string }) {
  const lead = tone === 'light' ? '#FFFFFF' : '#004D40';
  return (
    <svg
      viewBox="0 0 49 3"
      className={`block h-[3px] w-[49px] ${className}`}
      aria-hidden="true"
      focusable="false"
    >
      <rect x="0" y="0" width="20" height="3" rx="1.5" fill={lead} />
      <rect x="23" y="0" width="11" height="3" rx="1.5" fill="#FFC107" />
      <rect x="37" y="0" width="7" height="3" rx="1.5" fill="#00AEEF" />
      <rect x="47" y="0" width="2" height="3" rx="1" fill="#DF2F2B" />
    </svg>
  );
}

export default BrandRule;
